import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CORRIDORS, EV_TRUCK_DATA } from '@/constants/laadplein-data';

export function DemandTab() {
  const [trucks, setTrucks] = useState(5000);
  const [corridor, setCorridor] = useState('a2-north');
  const [pctStopt, setPctStopt] = useState(12);
  const [kwh, setKwh] = useState(250);
  const [duur, setDuur] = useState(45);
  const [tarief, setTarief] = useState(0.55);
  const [laders, setLaders] = useState(2);

  const corr = CORRIDORS.find(c => c.id === corridor);

  useEffect(() => {
    if (corr) setTrucks(corr.trucks_dag);
  }, [corridor, corr]);

  const jaren = EV_TRUCK_DATA.filter(d => d.jaar >= 2026).map(d => {
    const sessies = trucks * (d.pct_wagenpark / 100) * (pctStopt / 100);
    const totKwh = sessies * kwh;
    const omzet = totKwh * tarief;
    const maxSessies = Math.floor(24 * 60 / duur) * laders;
    return {
      ...d,
      sessies: Math.round(sessies * 10) / 10,
      kwh: Math.round(totKwh),
      omzet: Math.round(omzet),
      jaar_omzet: Math.round(omzet * 350),
      bezetting: Math.min(100, Math.round(sessies / maxSessies * 100)),
    };
  });

  return (
    <div>
      <div className="grid md:grid-cols-5 gap-4 mb-4">
        <Card className="md:col-span-2">
          <CardContent className="p-4">
            <div className="text-sm font-medium text-gray-600 mb-3">Parameters</div>

            <div className="mb-3">
              <Label className="text-xs text-gray-500">Corridor</Label>
              <Select value={corridor} onValueChange={setCorridor}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CORRIDORS.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.weg} {c.traject} ({c.trucks_dag.toLocaleString()})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <SliderField label="Trucks/dag" value={trucks} min={100} max={10000} step={100} onChange={setTrucks} />
            <SliderField label="% stopt bij ons" value={pctStopt} min={1} max={35} step={1} onChange={setPctStopt} unit="%" />
            <SliderField label="kWh/sessie" value={kwh} min={50} max={500} step={10} onChange={setKwh} unit=" kWh" />
            <SliderField label="Sessieduur" value={duur} min={15} max={120} step={5} onChange={setDuur} unit=" min" />
            <SliderField label="Tarief" value={tarief} min={0.30} max={0.80} step={0.01} onChange={setTarief} unit=" €/kWh" format={v => v.toFixed(2)} />
            <SliderField label="Aantal laders" value={laders} min={1} max={8} step={1} onChange={setLaders} />
          </CardContent>
        </Card>

        <Card className="md:col-span-3">
          <CardContent className="p-4">
            <div className="text-sm font-medium text-gray-600 mb-3">
              Forecast {corr?.weg} {corr?.traject}
            </div>

            <div className="space-y-1">
              <div className="grid grid-cols-6 text-xs text-gray-400 font-medium pb-1 border-b">
                <span>Jaar</span>
                <span className="text-right">% EV</span>
                <span className="text-right">Sessies/dag</span>
                <span className="text-right">kWh/dag</span>
                <span className="text-right">Omzet/jaar</span>
                <span className="text-right">Bezetting</span>
              </div>
              {jaren.map(j => (
                <div key={j.jaar} className={`grid grid-cols-6 text-xs py-1.5 border-b ${j.jaar <= 2026 ? "text-gray-400" : ""}`}>
                  <span className="font-medium">{j.jaar}</span>
                  <span className="text-right">{j.pct_wagenpark}%</span>
                  <span className="text-right font-medium">{j.sessies}</span>
                  <span className="text-right">{j.kwh.toLocaleString()}</span>
                  <span className={`text-right font-semibold ${j.jaar >= 2027 ? "text-emerald-600" : ""}`}>
                    €{j.jaar_omzet.toLocaleString()}
                  </span>
                  <span className="text-right">
                    <span className={`px-1.5 py-0.5 rounded text-xs ${
                      j.bezetting > 80 ? "bg-red-100 text-red-600"
                      : j.bezetting > 50 ? "bg-amber-100 text-amber-600"
                      : "bg-emerald-50 text-emerald-600"
                    }`}>
                      {j.bezetting}%
                    </span>
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-4 bg-gray-50 rounded-lg p-3">
              <div className="text-xs font-medium text-gray-500 mb-1">Visueel: sessies per dag over tijd ({laders} laders)</div>
              <div className="flex items-end gap-1 h-24">
                {jaren.map(j => {
                  const maxS = Math.max(...jaren.map(y => y.sessies));
                  const h = maxS > 0 ? (j.sessies / maxS) * 100 : 0;
                  return (
                    <div key={j.jaar} className="flex-1 flex flex-col items-center justify-end">
                      <div className="text-xs font-medium text-emerald-700 mb-0.5">
                        {j.sessies > 0 ? Math.round(j.sessies) : ''}
                      </div>
                      <div
                        className={`w-full rounded-t ${
                          j.bezetting > 80 ? "bg-red-400"
                          : j.bezetting > 50 ? "bg-amber-400"
                          : "bg-emerald-400"
                        }`}
                        style={{ height: `${Math.max(2, h)}%` }}
                      />
                      <div className="text-xs text-gray-400 mt-1">{j.jaar.toString().slice(2)}</div>
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-3 mt-2 text-xs text-gray-400 justify-center">
                <span className="flex items-center gap-1"><span className="w-2 h-2 bg-emerald-400 rounded inline-block" />OK</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 bg-amber-400 rounded inline-block" />&gt;50%</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 bg-red-400 rounded inline-block" />&gt;80% (uitbreiden)</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-700">
        <strong>Formule:</strong> {trucks.toLocaleString()} trucks/dag × {jaren[1]?.pct_wagenpark}% EV (2027) × {pctStopt}% stopt = <strong>{jaren[1]?.sessies}</strong> sessies/dag × {kwh} kWh × €{tarief.toFixed(2)} × 350 dgn = <strong className="text-emerald-700">€{jaren[1]?.jaar_omzet.toLocaleString()}/jaar</strong>
      </div>
    </div>
  );
}

function SliderField({ label, value, min, max, step, onChange, unit = '', format }: {
  label: string; value: number; min: number; max: number; step: number;
  onChange: (v: number) => void; unit?: string; format?: (v: number) => string;
}) {
  const display = format ? format(value) : value;
  return (
    <div className="mb-3">
      <div className="flex justify-between text-xs mb-1">
        <span className="text-gray-500">{label}</span>
        <span className="font-semibold text-emerald-700">{display}{unit}</span>
      </div>
      <Slider min={min} max={max} step={step} value={[value]} onValueChange={([v]) => onChange(v)} />
    </div>
  );
}
